import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Img from "react-image";

import { parseHTML, formatDate, getStoryLink } from "../../utilities";
import {
  colors,
  gradients,
  GridWrapper,
  Date,
  useMaxTabletPortrait,
} from "../../theme";
import Presentation from "../Presentation";
import AuthorCard from "../author/AuthorCard";
import Wysiwyg from "../Wysiwyg";
import applePodcast from "../../assets/ico-applepodcasts.svg";
import spotifyPodcast from "../../assets/ico-spotify.svg";
import overcastPodcast from "../../assets/ico-overcast.svg";
import castboxPodcast from "../../assets/ico-castbox.svg";
import googlePodcast from "../../assets/ico-googlepodcasts.svg";
import pocketcastsPodcast from "../../assets/ico-pocketcasts.svg";
import breakerPodcast from "../../assets/ico-breaker.svg";
import radiopublicPodcast from "../../assets/ico-radiopublic.svg";
import rssPodcast from "../../assets/ico-rss.svg";
import placeholderImg from "../../assets/placeholder-video-item.png";
import btnPlay from "../../assets/btn-play-red.svg";
import btnReadmore from "../../assets/btn-readmore-red.svg";

const AudioBackground = styled.div`
  height: 480px;
  overflow: hidden;
  position: absolute;
  width: 100%;

  @media (max-width: 1023px) {
    height: 385px;
  }

  @media (max-width: 767px) {
    height: 280px;
  }

  :before,
  :after {
    background: url(${(props) => props.background}) center no-repeat;
    background-size: cover;
    content: "";
    display: block;
    position: absolute;
  }

  :before {
    filter: blur(10px) brightness(0.6) contrast(134%);
    height: 104%;
    left: -2%;
    top: -2%;
    width: 104%;
  }

  :after {
    background-image: ${gradients.redToPurple("72deg")};
    filter: opacity(0.75) saturate(140%);
    height: 100%;
    width: 100%;
  }
`;

const AudioEmbed = styled.div`
  color: white;
  font-weight: 500;
  line-height: 2.4rem;
  margin: auto;
  padding: 80px 0 40px;
  position: relative;
  text-align: right;
  width: 810px;

  @media (max-width: 1023px) {
    padding: 80px 0 30px;
    width: 595px;
  }

  @media (max-width: 767px) {
    font-size: 1.3rem;
    padding: 30px 0 0;
    width: 300px;
  }

  iframe {
    height: 161px;
    width: 100%;

    @media (max-width: 1023px) {
      height: 98px;
    }
  }

  a {
    display: inline-block;
    margin: 5px 0 0 10px;

    img {
      height: 28px;
    }
  }
`;

const ProgramPresentation = styled(Presentation)`
  margin: 44px auto 80px;

  @media (max-width: 1023px) {
    margin: 30px auto 44px;
  }

  > a {
    padding: 8px 60px;
  }
`;

const Body = styled(GridWrapper)`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  padding-right: 99px;

  @media (max-width: 1599px) {
    padding-right: 0;
  }
`;

const SidebarBox = styled.div`
  border-bottom: 1px solid ${colors.borderColor};
  border-top: 1px solid ${colors.borderColor};
  margin-bottom: 50px;
  padding: 25px 0 35px;
  text-align: center;
  width: 184px;

  @media (max-width: 1599px) {
    width: 164px;
  }

  h1 {
    color: black;
    font-size: 2rem;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  a {
    box-shadow: 4px 8px 10px rgba(46, 68, 82, 0.2);
    display: block;
    position: relative;

    :before,
    :after {
      background: ${gradients.toDarkPurple("-30deg")};
      background-size: cover;
      content: "";
      display: block;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    :after {
      background: rgba(16, 0, 43, 0.5);
      opacity: 0;
      transition: 0.3s;
    }

    :hover {
      :after {
        opacity: 1;
      }

      div {
        transform: translate(-28px, -28px) scale(1.25);
      }
    }
  }

  img {
    display: block;
    width: 100%;
  }

  div {
    border-radius: 50%;
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
    height: 56px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-28px, -28px);
    transition: 0.3s;
    width: 56px;
    z-index: 1;
  }

  p {
    font-size: 1.4rem;
  }
`;

const Video = styled(SidebarBox)`
  div {
    background: calc(50% + 2px) center no-repeat url(${btnPlay}) white;
    background-size: 17px;
  }

  p {
    margin-top: 15px;
  }
`;

const Topics = styled(SidebarBox)`
  div {
    background: center no-repeat url(${btnReadmore}) white;
    background-size: 21px;
  }

  a {
    margin-top: 25px;
  }

  h2 {
    color: black;
    font-size: 1.7rem;
    font-weight: 500;
    margin-top: 15px;
  }
`;

const Main = styled.section`
  color: black;
  width: 778px;

  @media (max-width: 1599px) {
    width: 640px;
  }

  @media (max-width: 1023px) {
    width: 100%;
  }

  h1 {
    font-size: 3.8rem;
    font-weight: 500;
    line-height: 5.4rem;
    margin: 18px 0 44px;

    @media (max-width: 1023px) {
      font-size: 3.5rem;
      line-height: 4.5rem;
      margin-bottom: 38px;
    }

    @media (max-width: 767px) {
      font-size: 2.5rem;
      line-height: 3.5rem;
      margin-bottom: 35px;
    }

    ::first-letter {
      text-transform: uppercase;
    }
  }
`;

const Headline = styled.p`
  color: black;
  font-size: 2.2rem;
  font-weight: 500;
  line-height: 2.8rem;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const Credits = styled.p`
  border-left: 3px solid ${colors.primaryColor};
  color: ${colors.gray};
  font-style: italic;
  padding-left: 20px;
`;

function AudioPage({ audio }) {
  const isMaxTabletPortrait = useMaxTabletPortrait();

  const platforms = [
    { key: "apple_podcasts", label: "Apple Podcasts", img: applePodcast },
    { key: "spotify", label: "Spotify", img: spotifyPodcast },
    { key: "overcast", label: "Overcast", img: overcastPodcast },
    { key: "castbox", label: "Castbox", img: castboxPodcast },
    { key: "google_podcasts", label: "Google Podcasts", img: googlePodcast },
    { key: "pocket_casts", label: "Pocket Casts", img: pocketcastsPodcast },
    { key: "breaker", label: "Breaker", img: breakerPodcast },
    { key: "radiopublic", label: "RadioPublic", img: radiopublicPodcast },
    { key: "rss", label: "RSS", img: rssPodcast },
  ];

  return (
    <article>
      <AudioBackground background={audio.thumb_hero_url} />
      <AudioEmbed>
        <iframe
          src={`https://podcasters.spotify.com/pod/show/lemediatv/embed/episodes/${audio.attachments_by_keys.audio.value
            .split("episodes/")
            .pop()}`}
          frameBorder="0"
          scrolling="no"
        ></iframe>
        <div>
          Retrouvez ce podcast sur votre plateforme préférée
          <br />
          {platforms.map((platform) => {
            if (audio.primary_category.attributes[platform.key]) {
              return (
                <a
                  key={platform.key}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={platform.label}
                  href={audio.primary_category.attributes[platform.key]}
                >
                  <img src={platform.img} alt={platform.label} />
                </a>
              );
            }
            return false;
          })}
        </div>
      </AudioEmbed>
      <ProgramPresentation
        title={audio.primary_category.label}
        titleLink={`/podcasts/${audio.primary_category.slug}`}
        titleColor={colors.programColor}
        content={audio.primary_category.description}
        shareText={audio.title}
      />
      <Body>
        {!isMaxTabletPortrait && (
          <aside>
            <AuthorCard
              authors={audio.authors}
              tags={audio.story_tags}
              title={audio.title}
            />
            {audio.scheme === "VIDEO_AUDIO" && (
              <Video>
                <h1>Vidéo</h1>
                <Link to={getStoryLink(audio.canonical_url)}>
                  <Img
                    src={audio.thumb_hero_url}
                    alt={audio.title}
                    loader={<img src={placeholderImg} alt={audio.title} />}
                    unloader={<img src={placeholderImg} alt={audio.title} />}
                  />
                  <div />
                </Link>
                <p>Cette émission est aussi disponible en version vidéo.</p>
              </Video>
            )}
            {audio.topics.length > 0 && (
              <Topics>
                <h1>Dossiers</h1>
                <p>Vous pouvez retrouver ce podcast dans nos dossiers.</p>
                {audio.topics.map((topic) => (
                  <React.Fragment key={topic.slug}>
                    <Link to={`/dossiers/${topic.slug}`}>
                      <Img
                        src={topic.thumb_hero_url}
                        alt={topic.title}
                        loader={<img src={placeholderImg} alt={topic.title} />}
                        unloader={
                          <img src={placeholderImg} alt={topic.title} />
                        }
                      />
                      <div />
                    </Link>
                    <h2>{parseHTML(topic.rich_title)}</h2>
                  </React.Fragment>
                ))}
              </Topics>
            )}
          </aside>
        )}
        <Main>
          <Date>{formatDate(audio.published_at)}</Date>
          <h1>{parseHTML(audio.rich_title)}</h1>
          <Wysiwyg>
            {audio.headline && <Headline>{audio.headline}</Headline>}
            {parseHTML(audio.content)}
            {audio.cover_image_credits && (
              <Credits>{audio.cover_image_credits}</Credits>
            )}
          </Wysiwyg>
        </Main>
      </Body>
    </article>
  );
}

AudioPage.propTypes = {
  audio: PropTypes.shape({
    title: PropTypes.string.isRequired,
    attachments_by_keys: PropTypes.shape({
      audio: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    primary_category: PropTypes.shape({
      label: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      attributes: PropTypes.object.isRequired,
    }).isRequired,
    authors: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    story_tags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    published_at: PropTypes.string.isRequired,
    rich_title: PropTypes.string.isRequired,
    headline: PropTypes.string,
    content: PropTypes.string.isRequired,
    cover_image_credits: PropTypes.string,
    thumb_hero_url: PropTypes.string.isRequired,
    scheme: PropTypes.oneOf(["AUDIO", "VIDEO_AUDIO"]).isRequired,
    canonical_url: PropTypes.string.isRequired,
    topics: PropTypes.arrayOf(
      PropTypes.shape({
        slug: PropTypes.string.isRequired,
        thumb_hero_url: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        rich_title: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default AudioPage;
